/**
 * Microcopy admin form — Zod schemas mirroring the backend
 * microcopyResponse + the 5 max-length constants from
 * internal/tenant/microcopy/microcopy.go.
 *
 * Length caps are enforced both here (snappy client-side errors)
 * and by the backend (authoritative); the values match so an
 * over-long admin input fails inline instead of round-tripping
 * to a 400.
 */

import { z } from "zod";

export const MAX_PRODUCT_NAME = 60;
export const MAX_PRIMARY_METHOD_LABEL = 40;
export const MAX_BACKUP_LABEL = 40;
export const MAX_SUPPORT_HANDLE = 120;
export const MAX_SUPPORT_HELP_URL = 500;
// Phase G (P6).
export const MAX_PUREID_QR_CARD_TITLE = 60;

// The wire shape is TenantMicrocopy in api/openapi/admin.yaml, generated into api.generated.ts.
export { microcopyResponseSchema } from "./api.generated";
import type { microcopyResponseSchema } from "./api.generated";
export type MicrocopyResponse = z.infer<typeof microcopyResponseSchema>;

// Form-level validation: scheme allowlist for support_help_url.
// The backend P1-1 check is authoritative; this mirrors it for
// inline feedback before submit.
const URL_SCHEME_RE = /^https?:\/\//;

export const microcopyFormSchema = z
  .object({
    product_name: z.string().trim().max(MAX_PRODUCT_NAME),
    primary_method_label: z.string().trim().max(MAX_PRIMARY_METHOD_LABEL),
    backup_label: z.string().trim().max(MAX_BACKUP_LABEL),
    support_handle: z.string().trim().max(MAX_SUPPORT_HANDLE),
    support_help_url: z.string().trim().max(MAX_SUPPORT_HELP_URL),
    pureid_qr_card_title: z.string().trim().max(MAX_PUREID_QR_CARD_TITLE),
  })
  .superRefine((data, ctx) => {
    if (data.support_help_url && !URL_SCHEME_RE.test(data.support_help_url)) {
      ctx.addIssue({
        code: "custom",
        path: ["support_help_url"],
        message: "Must start with http:// or https://",
      });
    }
  });
export type MicrocopyFormValues = z.infer<typeof microcopyFormSchema>;
